import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../utils/api';
import { toast } from 'sonner';
import AppHeader from '../Header/AppHeader';
import './SubscriptionManager.css';

function SubscriptionManager() {
  const { t, i18n } = useTranslation();
  const isGerman = i18n.language === 'de';
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const data = await apiClient.getMySubscription();
      setSubscription(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setLoading(false);
    }
  };

  const handlePause = async () => {
    const confirmed = window.confirm(
      isGerman 
        ? 'Möchtest du dein Abonnement wirklich pausieren? Du kannst es jederzeit wieder fortsetzen.'
        : 'Do you really want to pause your subscription? You can resume it at any time.'
    );
    if (!confirmed) return;

    setActionLoading(true);
    try {
      await apiClient.pauseSubscription();
      toast.success(isGerman ? '✅ Abonnement pausiert' : '✅ Subscription paused', {
        description: isGerman 
          ? 'Dein Abo wurde erfolgreich pausiert. Du kannst es jederzeit wieder aktivieren.'
          : 'Your subscription has been paused. You can reactivate it at any time.',
        duration: 5000
      });
      fetchSubscription();
    } catch (error) {
      toast.error(isGerman ? '❌ Fehler beim Pausieren' : '❌ Error pausing', {
        description: error.response?.data?.detail || error.message,
        duration: 5000
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      await apiClient.resumeSubscription();
      toast.success(isGerman ? '✅ Abonnement fortgesetzt' : '✅ Subscription resumed', {
        description: isGerman 
          ? 'Dein Abo wurde erfolgreich fortgesetzt und ist wieder aktiv.'
          : 'Your subscription has been resumed and is active again.',
        duration: 5000
      });
      fetchSubscription();
    } catch (error) {
      toast.error(isGerman ? '❌ Fehler beim Fortsetzen' : '❌ Error resuming', {
        description: error.response?.data?.detail || error.message,
        duration: 5000
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    const confirmed = window.confirm(
      isGerman
        ? 'Möchtest du dein Abonnement wirklich kündigen?\n\n⚠️ Wichtig: Du behältst den Zugriff bis zum Ende deiner aktuellen Laufzeit.\nDanach verlierst du den Zugriff auf alle Premium-Features.'
        : 'Do you really want to cancel your subscription?\n\n⚠️ Important: You will keep access until the end of your current billing period.\nAfter that, you will lose access to all premium features.'
    );
    if (!confirmed) return;

    const reason = prompt(isGerman ? 'Grund für Kündigung (optional):' : 'Reason for cancellation (optional):');
    
    setActionLoading(true);
    try {
      await apiClient.cancelSubscription(reason);
      toast.success(isGerman ? '✅ Abonnement gekündigt' : '✅ Subscription cancelled', {
        description: isGerman 
          ? 'Dein Abo wurde gekündigt. Du kannst es bis zum Ende der Laufzeit weiterhin nutzen.'
          : 'Your subscription has been cancelled. You can continue using it until the end of the billing period.',
        duration: 6000
      });
      fetchSubscription();
    } catch (error) {
      toast.error(isGerman ? '❌ Fehler beim Kündigen' : '❌ Error cancelling', {
        description: error.response?.data?.detail || error.message,
        duration: 5000
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpgrade = (tier, interval) => {
    navigate('/pricing');
  };

  if (loading) {
    return (
      <>
        <AppHeader />
        <div className="subscription-manager" data-testid="subscription-manager">
          <div className="subscription-manager-loading">
            <div className="spinner"></div>
            <p>{isGerman ? 'Lade Abonnement...' : 'Loading subscription...'}</p>
          </div>
        </div>
      </>
    );
  }

  if (!subscription || subscription.status === 'none') {
    return (
      <>
        <AppHeader />
        <div className="subscription-manager" data-testid="subscription-manager">
          
          {/* Submenu Navigation */}
          <div className="account-submenu">
            <button 
              className="submenu-item"
              onClick={() => navigate('/account')}
            >
              {isGerman ? 'Profil bearbeiten' : 'Edit Profile'}
            </button>
            <button 
              className="submenu-item active"
              onClick={() => navigate('/subscription')}
            >
              {isGerman ? 'Mein Abo' : 'My Subscription'}
            </button>
            <button 
              className="submenu-item"
              onClick={() => navigate('/settings')}
            >
              {t('common.settings')}
            </button>
          </div>
          
          <div className="no-subscription">
          <h2>{isGerman ? 'Kein aktives Abonnement' : 'No active subscription'}</h2>
          <p>{isGerman ? 'Du hast aktuell kein aktives Abonnement.' : 'You currently don\'t have an active subscription.'}</p>
          <button onClick={() => navigate('/pricing')} className="btn-upgrade">
            {isGerman ? 'Jetzt upgraden' : 'Upgrade now'}
          </button>
        </div>
      </div>
      </>
    );
  }

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { label: isGerman ? 'Aktiv' : 'Active', color: 'green' },
      paused: { label: isGerman ? 'Pausiert' : 'Paused', color: 'yellow' },
      cancelled: { label: isGerman ? 'Gekündigt' : 'Cancelled', color: 'red' },
      expired: { label: isGerman ? 'Abgelaufen' : 'Expired', color: 'gray' },
      grace_period: { label: isGerman ? 'Zahlungsproblem' : 'Payment issue', color: 'orange' }
    };
    const config = statusMap[status] || { label: status, color: 'gray' };
    return <span className={`status-badge ${config.color}`}>{config.label}</span>;
  };

  return (
    <>
      <AppHeader />
      <div className="subscription-manager" data-testid="subscription-manager">
        
        {/* Submenu Navigation */}
        <div className="account-submenu">
          <button 
            className="submenu-item"
            onClick={() => navigate('/account')}
          >
            {isGerman ? 'Profil bearbeiten' : 'Edit Profile'}
          </button>
          <button 
            className="submenu-item active"
            onClick={() => navigate('/subscription')}
          >
            {isGerman ? 'Mein Abo' : 'My Subscription'}
          </button>
          <button 
            className="submenu-item"
            onClick={() => navigate('/settings')}
          >
            {t('common.settings')}
          </button>
        </div>

        {/* Page Header with Back Button */}
        <div className="subscription-page-header">
          <button className="btn-back" onClick={() => navigate('/dashboard')}>
            ← {isGerman ? 'Zurück zum Dashboard' : 'Back to Dashboard'}
          </button>
          <h1 className="page-title">{isGerman ? 'Mein Abonnement' : 'My Subscription'}</h1>
        </div>
        
        <div className="subscription-card">
          <div className="subscription-details">
            <div className="detail-row">
              <span className="detail-label">{isGerman ? 'Plan:' : 'Plan:'}</span>
              <span className="detail-value plan-value">
                {(subscription.subscription_tier || subscription.plan || 'STANDARD').toUpperCase()} {subscription.billing_interval === 'monthly' ? (isGerman ? 'Monatlich' : 'Monthly') : (isGerman ? 'Jährlich' : 'Yearly')}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">{isGerman ? 'Status:' : 'Status:'}</span>
              <span className="detail-value">{getStatusBadge(subscription.status)}</span>
            </div>
            
            {subscription.current_period_end && (
              <div className="detail-row">
                <span className="detail-label">{subscription.is_manual ? (isGerman ? 'Gültig bis:' : 'Valid until:') : (isGerman ? 'Nächste Abrechnung:' : 'Next billing:')}</span>
                <span className="detail-value">
                  {new Date(subscription.current_period_end).toLocaleDateString(isGerman ? 'de-DE' : 'en-US')}
                </span>
              </div>
            )}
            
            {subscription.status === 'cancelled' && subscription.access_until && (
              <div className="detail-row warning">
                <span className="detail-label">{isGerman ? 'Zugriff bis:' : 'Access until:'}</span>
                <span className="detail-value">
                  {new Date(subscription.access_until).toLocaleDateString(isGerman ? 'de-DE' : 'en-US')}
                </span>
              </div>
            )}

            <div className="detail-row">
              <span className="detail-label">{isGerman ? 'Zahlungsmethode:' : 'Payment method:'}</span>
              <span className="detail-value">
                {subscription.payment_method === 'manual' || subscription.is_manual
                  ? (isGerman ? 'Manuell zugewiesen / Banküberweisung' : 'Manually assigned / Bank Transfer')
                  : subscription.payment_method === 'trial'
                    ? (isGerman ? 'Testversion' : 'Trial')
                    : (subscription.payment_method || 'PayPal')}
              </span>
            </div>

            {/* Manual subscription info */}
            {subscription.is_manual && subscription.status === 'active' && (
              <div className="manual-subscription-info">
                <p className="manual-info-text">
                  ℹ️ {isGerman 
                    ? 'Dieses Abonnement wurde manuell zugewiesen oder per Banküberweisung bezahlt. Für Verlängerungen wende dich bitte an den Support.'
                    : 'This subscription was manually assigned or paid via bank transfer. For extensions, please contact support.'}
                </p>
                <button 
                  onClick={() => navigate('/pricing')} 
                  className="btn-upgrade-auto"
                >
                  💳 {isGerman ? 'Zu automatischem Abo upgraden' : 'Upgrade to automatic subscription'}
                </button>
              </div>
            )}
          </div>

          <div className="subscription-actions">
            {/* Only show pause/cancel for PayPal subscriptions, not manual/bank transfer */}
            {subscription.status === 'active' && !subscription.is_manual && (
              <>
                <button 
                  onClick={handlePause} 
                  className="btn-pause"
                  disabled={actionLoading}
                >
                  {actionLoading ? (isGerman ? 'Bitte warten...' : 'Please wait...') : (isGerman ? '⏸️ Pausieren' : '⏸️ Pause')}
                </button>
                <button 
                  onClick={handleCancel} 
                  className="btn-cancel"
                  disabled={actionLoading}
                >
                  {actionLoading ? (isGerman ? 'Bitte warten...' : 'Please wait...') : (isGerman ? '❌ Kündigen' : '❌ Cancel')}
                </button>
              </>
            )}
            
            {subscription.status === 'paused' && !subscription.is_manual && (
              <button 
                onClick={handleResume} 
                className="btn-resume"
                disabled={actionLoading}
              >
                {actionLoading ? (isGerman ? 'Bitte warten...' : 'Please wait...') : (isGerman ? '▶️ Fortsetzen' : '▶️ Resume')}
              </button>
            )}
            
            {(subscription.status === 'cancelled' || subscription.status === 'expired') && (
              <button 
                onClick={() => navigate('/pricing')} 
                className="btn-resubscribe"
              >
                {isGerman ? '🔄 Neu abonnieren' : '🔄 Resubscribe'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default SubscriptionManager;
